import { apiClient } from './client';

export interface LoginRequest {
  email: string;
  password: string;
}

export interface RegisterRequest {
  email: string;
  password: string;
  name: string;
}

export interface AuthUser {
  id: string;
  email: string;
  name?: string;
  firstName?: string;
  lastName?: string;
  currency?: string;
  timezone?: string;
  isActive: boolean;
  country?: string;
  emailVerified?: boolean;
  emailVerifiedAt?: string;
  onboardingStatus?: string;
  createdAt: string;
}

export interface AuthResponse {
  success: boolean;
  message: string;
  data: {
    user: AuthUser;
    tokens: {
      accessToken: string;
      refreshToken: string;
    };
    sessionId: string;
    entitlements: any;
    requiresEmailVerification?: boolean;
  };
}

export interface ErrorResponse {
  success: false;
  error: string;
}

export interface EmailVerificationResponse {
  success: boolean;
  message: string;
  data: {
    user: AuthUser;
    alreadyVerified: boolean;
  };
}

export interface VerificationStatusResponse {
  success: boolean;
  data: {
    email: string;
    emailVerified: boolean;
    emailVerifiedAt: string | null;
    hasPendingVerification: boolean;
    pendingTokenExpiresAt: string | null;
    canResendAfter: string | null;
  };
}

export interface ResendVerificationResponse {
  success: boolean;
  message: string;
  data: {
    email: string;
    expiresAt: string;
    canResendAfter: string | null;
    alreadyVerified?: boolean;
    user?: AuthUser;
  };
}

export interface MagicLinkResponse {
  success: boolean;
  message: string;
  data?: {
    canResendAfter: string | null;
  };
}

export interface MagicLinkVerifyResponse {
  success: boolean;
  message: string;
  data: {
    user: AuthUser;
    tokens: {
      accessToken: string;
      refreshToken: string;
    };
    sessionId: string;
    isNewUser: boolean;
    requiresEmailVerification: boolean;
  };
}

export interface ForgotPasswordResponse {
  success: boolean;
  message: string;
}

export interface ResetPasswordRequest {
  token: string;
  newPassword: string;
}

export interface ResetPasswordResponse {
  success: boolean;
  message: string;
}

class AuthService {
  async login(credentials: LoginRequest): Promise<AuthResponse> {
    const response = await apiClient.post('/auth/login', credentials);
    return response.data;
  }

  async register(userData: RegisterRequest): Promise<AuthResponse> {
    const response = await apiClient.post('/auth/register', userData);
    return response.data;
  }

  async socialLogin(provider: string, token: string): Promise<AuthResponse> {
    const response = await apiClient.post('/auth/social-login', { provider, token });
    return response.data;
  }

  async logout(): Promise<void> {
    try {
      await apiClient.post('/auth/logout');
    } catch (error) {
      // Even if logout fails on server, clear local state
      console.warn('Logout request failed, clearing local state anyway:', error);
    }
  }

  async refreshToken(refreshToken: string): Promise<AuthResponse> {
    const response = await apiClient.post('/auth/refresh', { refreshToken });
    return response.data;
  }

  async getCurrentUser(): Promise<AuthResponse> {
    const response = await apiClient.get('/auth/me');
    return response.data;
  }

  async verifyToken(token: string): Promise<AuthResponse> {
    const response = await apiClient.post('/auth/verify-token', { token });
    return response.data;
  }

  // ========== Email Verification Methods ==========

  /**
   * Verify email address using the token from the verification email
   */
  async verifyEmail(token: string): Promise<EmailVerificationResponse> {
    const response = await apiClient.post('/auth/verify-email', { token });
    return response.data;
  }

  /**
   * Get the current email verification status
   */
  async getVerificationStatus(): Promise<VerificationStatusResponse> {
    const response = await apiClient.get('/auth/verification-status');
    return response.data;
  }

  /**
   * Resend the verification email
   */
  async resendVerification(): Promise<ResendVerificationResponse> {
    const response = await apiClient.post('/auth/resend-verification');
    return response.data;
  }

  // ========== Magic Link Methods ==========

  /**
   * Send a magic link email for passwordless login
   */
  async sendMagicLink(email: string): Promise<MagicLinkResponse> {
    const response = await apiClient.post('/auth/magic-link', { email });
    return response.data;
  }

  /**
   * Verify magic link token and authenticate user
   */
  async verifyMagicLink(token: string): Promise<MagicLinkVerifyResponse> {
    const response = await apiClient.post('/auth/magic-link/verify', { token });
    return response.data;
  }

  // ========== Password Reset Methods ==========

  /**
   * Request a password reset email
   */
  async forgotPassword(email: string): Promise<ForgotPasswordResponse> {
    const response = await apiClient.post('/auth/forgot-password', { email });
    return response.data;
  }

  /**
   * Reset password using the token from the reset email
   */
  async resetPassword(data: ResetPasswordRequest): Promise<ResetPasswordResponse> {
    const response = await apiClient.post('/auth/reset-password', data);
    return response.data;
  }
}

export const authService = new AuthService();

